import L from 'leaflet';

/**
 * Status -> fill. These are the five states the icon set ships with, and the
 * single palette for everything that keys on status: the air/truck markers,
 * the vessel hull (via ShipMarker), the selected route, the Shipment status
 * chip's dots and the map legend.
 *
 * On time is sky, ahead is green and delayed is a lighter red per the latest
 * review.
 *
 * `at_port` has NO colour, deliberately. Waiting at a port is shown by the box
 * glyph, not by tinting a marker — ShipMarker already routes hub-waiting
 * vessels to `createHubIcon` without consulting this table, and giving the
 * state a colour as well meant the same thing was said twice, in two
 * different visual languages. Air and truck rows that report the state fall
 * back to `on_time` below rather than carrying an amber of their own.
 *
 * `discharged` keeps its grey: it was dropped from the legend and the filter,
 * but it is a real state a row can be in and a marker needs some fill.
 */
export const STATUS_COLORS = {
  on_time: '#0EA5E9',
  ahead: '#16A34A',
  delayed: '#F0284A',
  discharged: '#94A3B8',
};

/**
 * Outline for each fill — the darker companion the marker artwork strokes with.
 * These move with the fills above, or a recoloured marker keeps the old
 * outline: sky-blue "on time" ringed in dark green, which reads as a bug.
 */
export const STATUS_STROKES = {
  on_time: '#075985',
  ahead: '#14532D',
  delayed: '#8C132A',
  discharged: '#475569',
};

const WHEEL_FILL = '#2F3A33';

/**
 * Sub-paths per mode, in a 200x200 viewBox. `fixed` marks parts that keep their
 * own colour whatever the status (the truck's wheels), so recolouring never
 * turns them green. Exported so the map legend can draw the same glyphs the
 * markers use instead of keeping its own copy.
 */
export const MODE_SHAPES = {
  air: [
    {
      d: 'M100 19C107.6 19 112.6 31.4 112.6 47.4V74.8L188 119V137.6L112.6 118.4V151L134.2 166.8V180.2L100 170.6L65.8 180.2V166.8L87.4 151V118.4L12 137.6V119L87.4 74.8V47.4C87.4 31.4 92.4 19 100 19Z',
      strokeWidth: 11,
    },
  ],
  truck: [
    {
      d: 'M32 46H116C118.652 46 121.196 47.0536 123.071 48.9289C124.946 50.8043 126 53.3478 126 56V126H22C20.6868 126 19.3864 125.741 18.1732 125.239C16.9599 124.736 15.8575 124 14.9289 123.071C13.0536 121.196 12 118.652 12 116V56C12 53.3478 13.0536 50.8043 14.9289 48.9289C16.8043 47.0536 19.3478 46 22 46H32Z',
      strokeWidth: 11,
    },
    {
      d: 'M126 72H158C159.469 71.9976 160.921 72.319 162.252 72.9412C163.582 73.5634 164.76 74.4712 165.7 75.6L186 100C187.491 101.799 188.305 104.063 188.3 106.4V126H126V72Z',
      strokeWidth: 11,
    },
    {
      d: 'M71 153C80.3888 153 88 145.389 88 136C88 126.611 80.3888 119 71 119C61.6112 119 54 126.611 54 136C54 145.389 61.6112 153 71 153Z',
      strokeWidth: 9,
      fixed: WHEEL_FILL,
    },
    {
      d: 'M160 153C169.389 153 177 145.389 177 136C177 126.611 169.389 119 160 119C150.611 119 143 126.611 143 136C143 145.389 150.611 153 160 153Z',
      strokeWidth: 9,
      fixed: WHEEL_FILL,
    },
  ],
};

/**
 * Map a row to one of the five icon states.
 *
 * Air and truck rows only ever report in_transit / arrived — neither mode has
 * a delay signal, because neither has a carrier milestone feed to compare a
 * schedule against. `arrived` shows amber rather than grey on purpose: the
 * 4-day retention rule means the cargo is sitting AT the airport or crossing,
 * which is what "at port" depicts; grey "discharged" would imply it is gone.
 *
 * Defined in utils/shipmentChipFilters so the Shipment status chip can filter
 * on the same derivation without importing Leaflet; re-exported here under the
 * name the marker layers already use.
 */
export { shipmentStatusKey as statusToIconState } from '../../../utils/shipmentChipFilters';

/**
 * The plane points north in the artwork, so it rotates to its heading. The
 * truck is drawn in side view facing right: rotating it would put it on its
 * roof, so it mirrors instead when the heading points west.
 */
function transformFor(mode, heading) {
  const deg = Number.isFinite(heading) ? heading : 0;
  if (mode === 'truck') {
    return deg > 180 ? 'scale(-1,1) translate(-200,0)' : '';
  }
  return `rotate(${deg} 100 100)`;
}

/**
 * Leaflet divIcon for a mode marker.
 *
 * @param {object}  opts
 * @param {string}  opts.mode      'air' | 'truck'
 * @param {string}  opts.state     key of STATUS_COLORS
 * @param {number}  opts.heading   degrees, 0 = north
 * @param {boolean} opts.selected  draws the focus ring and a larger footprint
 */
const iconCache = new Map();

export function createModeIcon(opts = {}) {
  const { mode = 'air', state = 'on_time', heading = 0, selected = false, sizePx = null } = opts;
  const cacheKey = `${mode}|${state}|${Number.isFinite(heading) ? heading : 0}|${selected}|${sizePx}`;
  const hit = iconCache.get(cacheKey);
  if (hit) return hit;
  const icon = buildModeIcon({ mode, state, heading, selected, sizePx });
  iconCache.set(cacheKey, icon);
  return icon;
}

/**
 * Selected footprint, and how far the glyph shrinks to sit inside the ring.
 *
 * 58px matches the selected vessel exactly, so picking a plane and picking a
 * ship read as the same gesture. The glyph scales to 0.68 of the viewBox to
 * clear the inner ring — at the old 46px footprint that would have rendered it
 * *smaller* than the 34px resting marker, so the footprint has to grow with it.
 */
const SELECTED_SIZE = 58;
const SELECTED_GLYPH_SCALE = 0.68;

/**
 * The vessel's focus ring, restated in this icon set's 200-unit viewBox: the
 * ship draws r=22 / r=18 at stroke 3 / 1.5 in a 48 viewBox, so every value is
 * scaled by 200/48.
 */
const focusRing = (color) => `<circle cx="100" cy="100" r="91.7" fill="none" stroke="${color}" stroke-width="12.5" opacity="0.8"/>`
  + `<circle cx="100" cy="100" r="75" fill="none" stroke="${color}" stroke-width="6.25" opacity="0.4"/>`;

function buildModeIcon({ mode, state, heading, selected, sizePx }) {
  const shapes = MODE_SHAPES[mode] || MODE_SHAPES.air;
  const fill = STATUS_COLORS[state] || STATUS_COLORS.on_time;
  const size = sizePx || (selected ? SELECTED_SIZE : 34);
  const half = size / 2;
  const transform = transformFor(mode, heading);

  const paths = shapes.map((s) => (
    `<path d="${s.d}" fill="${s.fixed || fill}" stroke="white" stroke-width="${s.strokeWidth}" stroke-linejoin="round"/>`
  )).join('');

  // The ring must not rotate with the heading, so it sits outside the transform
  // group and the glyph is scaled by a wrapper around it.
  const glyph = selected
    ? `<g transform="translate(100 100) scale(${SELECTED_GLYPH_SCALE}) translate(-100 -100)">`
      + `<g transform="${transform}">${paths}</g></g>`
    : `<g transform="${transform}">${paths}</g>`;

  // --pin-glow carries the status colour to the hover rule in leafletStyles,
  // the same way the growing pins do, so a delayed vessel glows red rather
  // than every marker glowing one colour. `fill` comes from STATUS_COLORS in
  // this file, which the paths above already interpolate the same way.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 200 200" style="display:block;--pin-glow:${fill};">`
    + `${selected ? focusRing(fill) : ''}${glyph}</svg>`;

  return L.divIcon({
    html: svg,
    className: `mode-marker mode-marker--${mode} mode-marker--${state}`,
    iconSize: [size, size],
    iconAnchor: [half, half],
    popupAnchor: [0, -half],
  });
}

export default createModeIcon;
